import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Ship, Save, Plus, X, Upload, ExternalLink, TrendingUp, MapPin, CheckCircle, Circle, Award, Image, FileText, Globe, Building2, Palette, Users, Eye } from 'lucide-react';
import Header from '../components/Header';
import { toast } from 'sonner';
import api from '../utils/api';
import { toRelativeUrl, toFullUrl } from '../utils/imageUrl';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';

const CompanyProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [industryPartners, setIndustryPartners] = useState([]);

  const [formData, setFormData] = useState({
    legal_name: '',
    dba: '',
    description: '',
    website: '',
    logo_url: '',
    banner_url: '',
    company_video_url: '',
    services: [],
    modes: [],
    certifications: [],
    lanes_served: [],
    article_links: [],
    is_vessel_owner: false,
    is_vessel_broker: false,
    social_media: {
      linkedin: '',
      facebook: '',
      instagram: '',
      tiktok: ''
    }
  });

  const [newLane, setNewLane] = useState('');
  const [newCert, setNewCert] = useState('');
  const [newArticle, setNewArticle] = useState('');

  // Calculate profile completeness - must be before any conditional returns
  const profileCompletenessData = useMemo(() => {
    const criteria = [
      { key: 'logo', label: 'Company Logo', icon: Image, check: () => !!formData.logo_url, weight: 15 },
      { key: 'description', label: 'Company Description', icon: FileText, check: () => formData.description && formData.description.length >= 50, weight: 20 },
      { key: 'services', label: 'Services Listed', icon: Ship, check: () => formData.services && formData.services.length >= 1, weight: 15 },
      { key: 'modes', label: 'Transport Modes', icon: TrendingUp, check: () => formData.modes && formData.modes.length >= 1, weight: 10 },
      { key: 'website', label: 'Website URL', icon: Globe, check: () => !!formData.website, weight: 10 },
      { key: 'certifications', label: 'Certifications', icon: Award, check: () => formData.certifications && formData.certifications.length >= 1, weight: 15 },
      { key: 'lanes', label: 'Trade Lanes Served', icon: MapPin, check: () => formData.lanes_served && formData.lanes_served.length >= 1, weight: 10 },
      { key: 'banner', label: 'Banner Image', icon: Image, check: () => !!formData.banner_url, weight: 5 },
    ];

    let completedWeight = 0;
    const completed = [];
    const incomplete = [];

    criteria.forEach(item => {
      if (item.check()) {
        completedWeight += item.weight;
        completed.push(item);
      } else {
        incomplete.push(item);
      }
    });

    return {
      score: completedWeight,
      completed,
      incomplete,
      totalCriteria: criteria.length
    };
  }, [formData]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-500';
  };

  const getProgressColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  useEffect(() => {
    if (user?.company_id) {
      fetchCompany();
      fetchStatistics();
      fetchIndustryPartners();
    } else {
      setCompany({ error: 'no_company' });
    }
  }, [user]);

  const fetchCompany = async () => {
    try {
      const response = await api.get('/companies/me');
      // Check if admin response (no company)
      if (response.data.admin) {
        setCompany({ error: 'no_company' });
        return;
      }
      setCompany(response.data);
      setFormData({
        legal_name: response.data.legal_name || '',
        dba: response.data.dba || '',
        description: response.data.description || '',
        website: response.data.website || '',
        logo_url: response.data.logo_url || '',
        banner_url: response.data.banner_url || '',
        company_video_url: response.data.company_video_url || '',
        services: response.data.services || [],
        modes: response.data.modes || [],
        certifications: response.data.certifications || [],
        lanes_served: response.data.lanes_served || [],
        article_links: response.data.article_links || [],
        is_vessel_owner: response.data.is_vessel_owner || false,
        is_vessel_broker: response.data.is_vessel_broker || false,
        social_media: response.data.social_media || { linkedin: '', facebook: '', instagram: '', tiktok: '' }
      });
    } catch (error) {
      console.error('Failed to load company:', error);
      if (error.response?.status === 404) {
        setCompany({ error: 'no_company' });
      } else {
        setCompany({ error: 'load_failed', message: error.response?.data?.detail });
        toast.error(error.response?.data?.detail || 'Failed to load company data');
      }
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/companies/me/statistics');
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to load statistics');
    }
  };

  const fetchIndustryPartners = async () => {
    try {
      // Fetch companies with similar services
      const response = await api.get('/companies/partners');
      setIndustryPartners(response.data || []);
    } catch (error) {
      console.error('Failed to load industry partners');
    }
  };

  const handleImageUpload = async (file, field) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const result = await uploadToCloudinary(file, 'companies', token);
      
      // Store the Cloudinary URL directly (it's persistent across environments)
      setFormData(prev => ({ ...prev, [field]: result.url }));
      toast.success('Image uploaded successfully to cloud storage');
    } catch (error) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleCertificationUpload = async (file) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const result = await uploadToCloudinary(file, 'certifications', token);
      
      // Add to certifications array as an object with name and url
      const certObject = {
        name: file.name,
        url: result.url,
        type: file.type
      };
      
      setFormData(prev => ({
        ...prev,
        certifications: [...(prev.certifications || []), certObject]
      }));
      toast.success('Certification uploaded successfully to cloud storage');
    } catch (error) {
      toast.error(error.message || 'Failed to upload certification');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put('/companies/me', formData);
      toast.success('Profile updated successfully');
      setEditing(false);
      fetchCompany();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const addItem = (item, field, setter) => {
    if (item && !formData[field].includes(item)) {
      setFormData({ ...formData, [field]: [...formData[field], item] });
      setter('');
    }
  };

  const removeItem = (item, field) => {
    setFormData({ ...formData, [field]: formData[field].filter(i => i !== item) });
  };

  if (!company) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  if (company.error === 'no_company') {
    // Check if user is admin
    const isAdmin = user?.role === 'admin';
    
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 p-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>{isAdmin ? 'Admin Account' : 'No Company Profile'}</CardTitle>
              </CardHeader>
              <CardContent>
                {isAdmin ? (
                  <>
                    <p className="text-slate-600 mb-4">
                      You are logged in as a <strong>CTC Network Administrator</strong>. Admin accounts do not require a company profile.
                    </p>
                    <p className="text-sm text-slate-500 mb-6">
                      As an admin, you have full access to approve applications, manage members, and oversee all network activity.
                    </p>
                    <div className="flex gap-3">
                      <Button onClick={() => window.location.href = '/admin'} className="bg-cyan-500 hover:bg-cyan-600">
                        Go to Admin Dashboard
                      </Button>
                      <Button onClick={() => window.location.href = '/admin/applications'} variant="outline">
                        Review Applications
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-slate-600 mb-4">
                      Your company profile is being set up. You can start building your profile right away!
                    </p>
                    <p className="text-sm text-slate-500 mb-4">
                      If you just registered, please refresh the page or contact support if the issue persists.
                    </p>
                    <Button onClick={() => window.location.reload()} className="bg-cyan-500 hover:bg-cyan-600">
                      Refresh Page
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  if (company.error === 'load_failed') {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 p-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600">Error Loading Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">{company.message || 'Failed to load company profile'}</p>
                <Button onClick={fetchCompany} className="bg-cyan-500 hover:bg-cyan-600">
                  Try Again
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  const isPending = company.status === 'pending';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto p-8">
          {/* Profile Completeness Score Card */}
          <Card className="mb-6 overflow-hidden">
            <div className={`h-2 ${getProgressColor(profileCompletenessData.score)}`} style={{ width: `${profileCompletenessData.score}%` }}></div>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                {/* Score Circle */}
                <div className="flex-shrink-0 flex items-center gap-4">
                  <div className="relative w-24 h-24">
                    <svg className="w-24 h-24 transform -rotate-90">
                      <circle
                        cx="48"
                        cy="48"
                        r="40"
                        stroke="#e2e8f0"
                        strokeWidth="8"
                        fill="none"
                      />
                      <circle
                        cx="48"
                        cy="48"
                        r="40"
                        stroke={profileCompletenessData.score >= 80 ? '#22c55e' : profileCompletenessData.score >= 50 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="8"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${profileCompletenessData.score * 2.51} 251`}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className={`text-2xl font-bold ${getScoreColor(profileCompletenessData.score)}`}>
                        {profileCompletenessData.score}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900">Profile Completeness</h3>
                    <p className="text-sm text-slate-600">
                      {profileCompletenessData.score >= 80 ? 'Excellent! Your profile stands out.' :
                       profileCompletenessData.score >= 50 ? 'Good progress! A few more items to go.' :
                       'Complete your profile to attract more business.'}
                    </p>
                    {isPending && profileCompletenessData.score < 80 && (
                      <Badge className="mt-2 bg-amber-100 text-amber-700">
                        Complete profile for faster approval
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Checklist */}
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {profileCompletenessData.completed.map((item) => (
                    <div key={item.key} className="flex items-center gap-2 text-sm p-2 bg-green-50 rounded-lg">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-green-700 truncate">{item.label}</span>
                    </div>
                  ))}
                  {profileCompletenessData.incomplete.map((item) => (
                    <div 
                      key={item.key} 
                      className="flex items-center gap-2 text-sm p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => !editing && setEditing(true)}
                    >
                      <Circle className="h-4 w-4 text-slate-300 flex-shrink-0" />
                      <span className="text-slate-500 truncate">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pending Banner */}
              {isPending && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-4 py-3 rounded-lg">
                    <Building2 className="h-5 w-5 flex-shrink-0" />
                    <div>
                      <span className="font-medium">Application Pending Review</span>
                      <span className="text-sm ml-2">• A complete profile increases your chances of faster approval</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-outfit font-bold text-ocean-900">Company Profile</h1>
              <div className="text-slate-600 mt-2 flex items-center gap-2 flex-wrap">
                <span>Status:</span>
                <Badge className={
                  company.status === 'active' ? 'bg-green-100 text-green-700' :
                  company.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                  'bg-red-100 text-red-700'
                }>
                  {company.status.toUpperCase()}
                </Badge>
                {isPending && <span className="text-sm text-slate-500">• You can still edit your profile</span>}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* View Public Profile */}
              {company?.status === 'active' && (
                <Button 
                  onClick={() => navigate(`/company/${company.id}`)} 
                  variant="outline"
                  className="gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View Public Profile
                </Button>
              )}
              {/* Profile Editor - Mini Website Customization */}
              <Button 
                onClick={() => navigate('/profile/edit')} 
                variant="outline"
                className="gap-2 border-cyan-200 text-cyan-700 hover:bg-cyan-50"
              >
                <Palette className="h-4 w-4" />
                Customize Mini-Website
              </Button>
              {!editing ? (
                <Button onClick={() => setEditing(true)} className="bg-cyan-500 hover:bg-cyan-600" data-testid="edit-profile-button">
                  {isPending ? 'Complete Your Profile' : 'Edit Profile'}
                </Button>
              ) : (
                <>
                  <Button onClick={() => { setEditing(false); fetchCompany(); }} variant="outline">
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={loading} className="bg-green-500 hover:bg-green-600" data-testid="save-profile-button">
                    <Save className="mr-2 h-4 w-4" />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Banner Image */}
          {(editing || formData.banner_url) && (
            <Card className="mb-6">
              <CardContent className="p-6">
                {formData.banner_url && (
                  <img src={toFullUrl(formData.banner_url)} alt="Company Banner" className="w-full h-48 object-cover rounded-lg mb-4" />
                )}
                {editing && (
                  <div>
                    <Label>Company Banner</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'banner_url')}
                      disabled={uploading}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {/* Logo */}
            <Card>
              <CardContent className="p-6 text-center">
                {toFullUrl(formData.logo_url) ? (
                  <img src={toFullUrl(formData.logo_url)} alt="Company Logo" className="w-32 h-32 object-contain mx-auto mb-4 rounded" />
                ) : (
                  <div className="w-32 h-32 bg-slate-100 rounded mx-auto mb-4 flex items-center justify-center">
                    <Ship className="h-16 w-16 text-slate-400" />
                  </div>
                )}
                {editing && (
                  <div>
                    <Label>Company Logo</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'logo_url')}
                      disabled={uploading}
                      className="mt-2"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Statistics */}
            {statistics && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Quotes Completed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold text-green-500">{statistics.total_quotes_completed}</div>
                    <p className="text-sm text-slate-600 mt-2">Total submitted: {statistics.total_quotes_submitted}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Routes Serviced</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold text-cyan-500">{statistics.verified_routes.length}</div>
                    <p className="text-sm text-slate-600 mt-2">Unique routes</p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Legal Name</Label>
                  {editing ? (
                    <Input
                      value={formData.legal_name}
                      onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                    />
                  ) : (
                    <p className="text-slate-900 font-medium">{company.legal_name}</p>
                  )}
                </div>
                <div>
                  <Label>DBA (Doing Business As)</Label>
                  {editing ? (
                    <Input
                      value={formData.dba}
                      onChange={(e) => setFormData({ ...formData, dba: e.target.value })}
                      placeholder="Trade name"
                    />
                  ) : (
                    <p className="text-slate-900">{company.dba || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <Label>Website</Label>
                  {editing ? (
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://www.example.com"
                    />
                  ) : (
                    company.website ? (
                      <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline flex items-center gap-1">
                        {company.website} <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : <p className="text-slate-600">N/A</p>
                  )}
                </div>
                <div>
                  <Label>Description</Label>
                  {editing ? (
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      placeholder="Tell us about your company..."
                    />
                  ) : (
                    <p className="text-slate-900">{company.description || 'No description'}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Services & Modes */}
            <Card>
              <CardHeader>
                <CardTitle>Services & Capabilities</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Service Types</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(editing ? formData.services : company.services).map(service => (
                      <Badge key={service} className="bg-cyan-100 text-cyan-700">
                        {service}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Transport Modes</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(editing ? formData.modes : company.modes).map(mode => (
                      <Badge key={mode} className="bg-blue-100 text-blue-700">
                        {mode}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Certifications, Licenses & Bonds</Label>
                  <p className="text-xs text-slate-500 mb-2">Upload photos or PDFs of your certifications, licenses, and bonds</p>
                  <div className="space-y-2 mt-2">
                    {(editing ? formData.certifications : company.certifications || []).map((cert, idx) => (
                      <div key={idx} className="flex items-center justify-between px-3 py-2 bg-green-50 rounded">
                        {typeof cert === 'object' ? (
                          <a 
                            href={cert.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-green-700 hover:underline flex items-center gap-2"
                          >
                            {cert.type?.includes('pdf') ? (
                              <FileText className="h-4 w-4" />
                            ) : (
                              <Image className="h-4 w-4" />
                            )}
                            {cert.name}
                          </a>
                        ) : (
                          <span className="text-sm text-green-700 flex items-center gap-2">
                            <Award className="h-4 w-4" />
                            {cert}
                          </span>
                        )}
                        {editing && (
                          <button 
                            onClick={() => {
                              const certs = formData.certifications.filter((_, i) => i !== idx);
                              setFormData({ ...formData, certifications: certs });
                            }} 
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    {editing && (
                      <div className="space-y-3 pt-2">
                        {/* Upload file option */}
                        <div className="flex items-center gap-2">
                          <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-400 hover:bg-cyan-50 transition-colors">
                            <Upload className="h-5 w-5 text-slate-400" />
                            <span className="text-sm text-slate-600">Upload Certificate/License (PDF or Image)</span>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleCertificationUpload(e.target.files[0]);
                                  e.target.value = '';
                                }
                              }}
                              className="hidden"
                              disabled={uploading}
                            />
                          </label>
                        </div>
                        {/* Or add text certification */}
                        <div className="flex gap-2">
                          <Input
                            value={newCert}
                            onChange={(e) => setNewCert(e.target.value)}
                            placeholder="Or type certification name (e.g., IATA, FIATA, C-TPAT)"
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem(newCert, 'certifications', setNewCert))}
                          />
                          <Button onClick={() => addItem(newCert, 'certifications', setNewCert)} size="sm" variant="outline">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Routes/Lanes */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Service Lanes & Routes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                {(editing ? formData.lanes_served : company.lanes_served).map(lane => (
                  <div key={lane} className="flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                    <MapPin className="h-3 w-3" />
                    <span>{lane}</span>
                    {editing && (
                      <button onClick={() => removeItem(lane, 'lanes_served')} className="ml-1 text-purple-700 hover:text-purple-900">
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {editing && (
                <div className="flex gap-2">
                  <Input
                    value={newLane}
                    onChange={(e) => setNewLane(e.target.value)}
                    placeholder="e.g., MIA-NAS, KIN-POS"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem(newLane, 'lanes_served', setNewLane))}
                  />
                  <Button onClick={() => addItem(newLane, 'lanes_served', setNewLane)} size="sm" variant="outline">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {statistics && statistics.top_routes.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-semibold text-sm mb-3">Top Completed Routes:</h4>
                  <div className="space-y-2">
                    {statistics.top_routes.map((route, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{route.route}</span>
                        <Badge variant="secondary">{route.count} completed</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Charter Services - Vessel Owner/Broker */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ship className="h-5 w-5 text-cyan-500" />
                Charter Services
              </CardTitle>
              <p className="text-sm text-slate-500">
                Enable these options if your company provides vessel charter services
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {editing ? (
                <div className="space-y-4">
                  {/* Vessel Owner Toggle */}
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex-shrink-0 pt-1">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, is_vessel_owner: !formData.is_vessel_owner })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          formData.is_vessel_owner ? 'bg-cyan-500' : 'bg-slate-300'
                        }`}
                        data-testid="vessel-owner-toggle"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.is_vessel_owner ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Label className="text-base font-semibold">Vessel Owner</Label>
                        {formData.is_vessel_owner && (
                          <Badge className="bg-cyan-100 text-cyan-700">Active</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        Your company owns vessels and can offer them for charter. You'll be able to manage your fleet and respond to Charter RFQs.
                      </p>
                    </div>
                  </div>

                  {/* Vessel Broker Toggle */}
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex-shrink-0 pt-1">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, is_vessel_broker: !formData.is_vessel_broker })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          formData.is_vessel_broker ? 'bg-cyan-500' : 'bg-slate-300'
                        }`}
                        data-testid="vessel-broker-toggle"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.is_vessel_broker ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Label className="text-base font-semibold">Vessel Broker</Label>
                        {formData.is_vessel_broker && (
                          <Badge className="bg-purple-100 text-purple-700">Active</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        Your company brokers vessel charters on behalf of vessel owners. You can submit quotes using vessels from your network.
                      </p>
                    </div>
                  </div>

                  {(formData.is_vessel_owner || formData.is_vessel_broker) && (
                    <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                      <p className="text-sm text-cyan-800">
                        <strong>Next Steps:</strong> After saving, visit the{' '}
                        <a href="/charters" className="underline font-medium">Charters</a> section to manage your fleet 
                        {formData.is_vessel_owner && ' and add your vessels'}. You'll then be able to respond to Charter RFQs from network members.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${company.is_vessel_owner ? 'bg-cyan-500' : 'bg-slate-300'}`} />
                    <span className={company.is_vessel_owner ? 'text-slate-900 font-medium' : 'text-slate-500'}>
                      Vessel Owner
                    </span>
                    {company.is_vessel_owner && (
                      <Badge className="bg-cyan-100 text-cyan-700 text-xs">Active</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${company.is_vessel_broker ? 'bg-purple-500' : 'bg-slate-300'}`} />
                    <span className={company.is_vessel_broker ? 'text-slate-900 font-medium' : 'text-slate-500'}>
                      Vessel Broker
                    </span>
                    {company.is_vessel_broker && (
                      <Badge className="bg-purple-100 text-purple-700 text-xs">Active</Badge>
                    )}
                  </div>
                  {!company.is_vessel_owner && !company.is_vessel_broker && (
                    <p className="text-sm text-slate-500 mt-2">
                      Not currently offering charter services. Click "Edit Profile" to enable.
                    </p>
                  )}
                  {(company.is_vessel_owner || company.is_vessel_broker) && (
                    <Button 
                      onClick={() => navigate('/fleet-management')} 
                      variant="outline" 
                      className="mt-3 gap-2"
                    >
                      <Ship className="h-4 w-4" />
                      Manage Fleet
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Article Links */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>News & Articles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-4">
                {(editing ? formData.article_links : company.article_links || []).map((link, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                    <a href={link} target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline flex items-center gap-2 text-sm">
                      <ExternalLink className="h-4 w-4" />
                      {link}
                    </a>
                    {editing && (
                      <button onClick={() => removeItem(link, 'article_links')} className="text-red-500 hover:text-red-700">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {editing && (
                <div className="flex gap-2">
                  <Input
                    value={newArticle}
                    onChange={(e) => setNewArticle(e.target.value)}
                    placeholder="https://news.example.com/article"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem(newArticle, 'article_links', setNewArticle))}
                  />
                  <Button onClick={() => addItem(newArticle, 'article_links', setNewArticle)} size="sm" variant="outline">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Social Media */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Social Media</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {editing ? (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>LinkedIn</Label>
                      <Input
                        value={formData.social_media?.linkedin || ''}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          social_media: { ...formData.social_media, linkedin: e.target.value }
                        })}
                        placeholder="https://linkedin.com/company/..."
                        data-testid="social-linkedin-input"
                      />
                    </div>
                    <div>
                      <Label>Facebook</Label>
                      <Input
                        value={formData.social_media?.facebook || ''}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          social_media: { ...formData.social_media, facebook: e.target.value }
                        })}
                        placeholder="https://facebook.com/..."
                        data-testid="social-facebook-input"
                      />
                    </div>
                    <div>
                      <Label>Instagram</Label>
                      <Input
                        value={formData.social_media?.instagram || ''}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          social_media: { ...formData.social_media, instagram: e.target.value }
                        })}
                        placeholder="https://instagram.com/..."
                        data-testid="social-instagram-input"
                      />
                    </div>
                    <div>
                      <Label>TikTok</Label>
                      <Input
                        value={formData.social_media?.tiktok || ''}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          social_media: { ...formData.social_media, tiktok: e.target.value }
                        })}
                        placeholder="https://tiktok.com/@..."
                        data-testid="social-tiktok-input"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {company.social_media?.linkedin && (
                    <a href={company.social_media.linkedin.startsWith('http') ? company.social_media.linkedin : `https://${company.social_media.linkedin}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-cyan-600 hover:underline">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                      LinkedIn
                    </a>
                  )}
                  {company.social_media?.facebook && (
                    <a href={company.social_media.facebook.startsWith('http') ? company.social_media.facebook : `https://${company.social_media.facebook}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-cyan-600 hover:underline">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385h-3.047v-3.47h3.047v-2.642c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385c5.737-.9 10.125-5.864 10.125-11.854z"/></svg>
                      Facebook
                    </a>
                  )}
                  {company.social_media?.instagram && (
                    <a href={company.social_media.instagram.startsWith('http') ? company.social_media.instagram : `https://${company.social_media.instagram}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-cyan-600 hover:underline">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                      Instagram
                    </a>
                  )}
                  {company.social_media?.tiktok && (
                    <a href={company.social_media.tiktok.startsWith('http') ? company.social_media.tiktok : `https://${company.social_media.tiktok}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-cyan-600 hover:underline">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                      TikTok
                    </a>
                  )}
                  {!company.social_media?.linkedin && !company.social_media?.facebook && !company.social_media?.instagram && !company.social_media?.tiktok && (
                    <p className="text-slate-500 col-span-4">No social media links added</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Industry Partners */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-cyan-500" />
                Industry Partners
              </CardTitle>
              <p className="text-sm text-slate-500">Companies in similar industries</p>
            </CardHeader>
            <CardContent>
              {industryPartners.length > 0 ? (
                <div className="grid gap-3">
                  {industryPartners.map((partner) => (
                    <div 
                      key={partner.id} 
                      className="flex items-center gap-4 p-3 rounded-lg border border-slate-200 hover:border-cyan-300 hover:bg-slate-50 transition-all cursor-pointer"
                      onClick={() => window.open(`/company/${partner.id}`, '_blank')}
                      data-testid={`partner-${partner.id}`}
                    >
                      {/* Partner Logo */}
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-cyan-500 to-ocean-600 flex items-center justify-center text-white font-bold text-lg overflow-hidden flex-shrink-0">
                        {toFullUrl(partner.logo_url) ? (
                          <img src={toFullUrl(partner.logo_url)} alt={partner.legal_name} className="h-full w-full object-cover" />
                        ) : (
                          partner.legal_name?.charAt(0) || 'C'
                        )}
                      </div>
                      
                      {/* Partner Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 truncate">{partner.legal_name || partner.dba || 'Company'}</h4>
                        <p className="text-sm text-slate-500 truncate">{partner.country || 'Caribbean'}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {partner.services?.slice(0, 3).map((service, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs bg-cyan-50 text-cyan-700">
                              {service}
                            </Badge>
                          ))}
                          {partner.services?.length > 3 && (
                            <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-600">
                              +{partner.services.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* View Profile Button */}
                      <Button variant="ghost" size="sm" className="text-cyan-600 hover:text-cyan-700 flex-shrink-0">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No industry partners found yet.</p>
                  <p className="text-sm mt-1">Partners will appear here based on your company&apos;s services.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default CompanyProfile;
